/**
 * Página de estado de cierre de cuenta.
 * Muestra el estado actual del proceso de cierre con polling automático.
 *
 * @taskId T466
 * @see functional/Specs/specs_cycle_4.md HU-16-FE
 */

import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button, Eyebrow } from '@flexibilitypay/ui';

import { ClosureStatusPanel } from '../features/closure';

/**
 * Página de estado de cierre de cuenta.
 *
 * Accesible por:
 * - PSP_OPERATOR: solo lectura
 * - PSP_ADMIN: lectura (acciones de escritura no incluidas en MVP)
 *
 * @see HU-16-FE
 */
export function ClosureStatusPage() {
  const { t } = useTranslation(['closure', 'common']);
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId: string }>();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleBack = useCallback(() => {
    // Volver al listado de cuentas o detalle
    navigate(-1);
  }, [navigate]);

  const handleGoToAccounts = useCallback(() => {
    navigate('/accounts');
  }, [navigate]);

  const handleClosed = useCallback(() => {
    // Opcional: mostrar toast o navegar cuando el cierre se complete
    // Por ahora no hacemos nada, el panel ya muestra el estado CLOSED
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!accountId) {
    return (
      <div className="container py-6">
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <FileText className="h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">
            {t('closure:page.noAccountId', 'No se especificó una cuenta')}
          </p>
          <Button variant="outline" onClick={handleGoToAccounts}>
            {t('closure:page.goToAccounts', 'Ir a Cuentas')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6">
      {/* Breadcrumb + Header */}
      <div className="mb-6">
        <Eyebrow>{t('closure:page.breadcrumb', 'Backoffice Operativo / Cuentas')}</Eyebrow>

        <div className="mt-2 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label={t('common:back', 'Volver')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t('closure:page.title', 'Estado de Cierre')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('closure:page.subtitle', 'Seguimiento del proceso de cierre de cuenta')}
            </p>
          </div>
        </div>
      </div>

      {/* Panel de estado con polling */}
      <div className="max-w-2xl">
        <ClosureStatusPanel
          accountId={accountId}
          enableAutoRefresh={true}
          onClosed={handleClosed}
        />
      </div>

      {/* Info adicional */}
      <div className="mt-6 max-w-2xl rounded-md border bg-muted/30 p-4">
        <h3 className="font-medium">{t('closure:page.infoTitle', '¿Qué significa cada estado?')}</h3>
        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
          <li>
            <strong className="text-yellow-700">{t('closure:status.CLOSURE_REQUESTED', 'Solicitud de cierre')}</strong>:{' '}
            {t('closure:page.infoRequested', 'El proceso de cierre ha sido iniciado.')}
          </li>
          <li>
            <strong className="text-orange-700">{t('closure:status.PENDING_FUND_TRANSFER', 'Pendiente transferencia')}</strong>:{' '}
            {t('closure:page.infoPending', 'Se está procesando la restitución de fondos.')}
          </li>
          <li>
            <strong className="text-blue-700">{t('closure:status.FUND_TRANSFERRED', 'Fondos transferidos')}</strong>:{' '}
            {t('closure:page.infoTransferred', 'Los fondos fueron transferidos exitosamente.')}
          </li>
          <li>
            <strong className="text-green-700">{t('closure:status.CLOSED', 'Cerrada')}</strong>:{' '}
            {t('closure:page.infoClosed', 'La cuenta ha sido cerrada definitivamente.')}
          </li>
          <li>
            <strong className="text-red-700">{t('closure:status.CLOSURE_FAILED', 'Cierre fallido')}</strong>:{' '}
            {t('closure:page.infoFailed', 'Hubo un error en el proceso. Contacte soporte.')}
          </li>
        </ul>
      </div>
    </div>
  );
}
